/** @jsx jsx */
import { useContext, useState, useRef } from 'react';
import { Box, Flex, Text, jsx } from 'theme-ui';
import { animated, useTransition, useSpring, useChain } from 'react-spring';
import { BsFillBagFill } from 'react-icons/bs';
import LineItem from './line-item/line-item';
import { CartContext } from '../../provider/cart-provider';
import Cart from './cart';

const CartStatus = () => {
	const { store } = useContext(CartContext);
	const { checkout } = store;
	const [open, set] = useState<boolean>(false);
	const cartBtnProps = useSpring({
		config: { mass: 1, tension: 500, friction: 48 },
		opacity: open ? 0 : 1,
		height: open ? '20px' : '50px',
		bottom: open ? '-100%' : '30px',
	});
	const cartOverlayRef = useRef();
	const { size, ...rest } = useSpring({
		ref: cartOverlayRef,
		config: { mass: 1, tension: 500, friction: 50 },
		from: { size: '30%', left: '35%', opacity: 0, bottom: '30px' },
		to: {
			opacity: open ? 1 : 0,
			size: open ? '100%' : '30%',
			left: open ? '0' : '35%',
			bottom: open ? '0' : '30px',
		},
	});
	const productList = useTransition(
		open ? checkout?.lineItems : [],
		(item) => item.id,
		{
			unique: true,
			trail: 400 / checkout?.lineItems.length,
			from: {
				opacity: 0,
				transform: 'scale(0.98)',
				marginRight: '-100%',
			},
			enter: {
				opacity: 1,
				transform: 'scale(1)',
				marginRight: '0',
			},
			leave: {
				opacity: 0,
				transform: 'scale(0.98)',
				marginRight: '-100%',
			},
		}
	);
	useChain(
		open
			? [cartBtnProps, cartOverlayRef, productList]
			: [productList, cartOverlayRef, cartBtnProps],
		[0, open ? 0.1 : 0.2]
	);
	const getPrice = (price) =>
		Intl.NumberFormat(undefined, {
			currency: price.currencyCode,
			minimumFractionDigits: 2,
			style: 'currency',
		}).format(parseFloat(price && price.amount ? price.amount : 0));

	return (
		<Box sx={styles.wrapper}>
			{checkout?.lineItems.length && (
				<animated.button
					style={{ ...cartBtnProps, ...styles.cartBtn }}
					onClick={() => set(!open)}
				>
					<Flex sx={styles.cartText}>
						<BsFillBagFill size="20px" />
						Added {checkout?.lineItems.length} Items
					</Flex>
					<Text sx={styles.price}>
						{checkout.totalPriceV2 && getPrice(checkout.totalPriceV2)}
					</Text>
				</animated.button>
			)}

			<animated.div
				style={{
					...rest,
					width: size,
					height: size,
					position: 'fixed',
					backgroundColor: 'white',
					pointerEvents: open ? 'all' : 'none',
					zIndex: 9999,
				}}
			>
				<Cart onClick={() => set(false)}>
					<Box>
						{checkout?.lineItems.length > 0 ? (
							productList.map(({ item, key, props }) => (
								<animated.div key={key} style={{ ...props }}>
									<LineItem item={item} />
								</animated.div>
							))
						) : (
							<Box sx={styles.notFound}>Products are not available</Box>
						)}
					</Box>
				</Cart>
			</animated.div>
		</Box>
	);
};

const styles = {
	wrapper: {
		width: '100%',
		maxWidth: 1000,
		margin: '0 auto',
		padding: '0 20px',
		pointerEvents: 'none',
	},
	cartBtn: {
		outline: 0,
		width: '100%',
		maxWidth: 960,
		position: 'fixed',
		pointerEvents: 'all',
		color: 'white',
		border: 0,
		padding: '0 30px',
		borderRadius: 6,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: '#292929',
		boxShadow: '0 6px 12px rgba(0, 0, 0, 0.12)',
		cursor: 'pointer',
		left: 'auto',
		right: 'auto',
		zIndex: 999,
	},
	cartText: {
		alignItems: 'center',
		fontSize: '15px',
		svg: {
			marginRight: 15,
		},
	},
	price: {
		fontSize: '15px',
		fontWeight: 500,
	},
	notFound: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontWeight: 700,
	},
};

export default CartStatus;
